import {
  ButtonComponent,
  ButtonSubTheme,
  ButtonTheme,
  ButtonType,
} from '@myre/ui-primitive-components';
import { ActionIdDirective, TranslatePipe } from '@myre/ui-primitive-core';

import { AsyncPipe } from '@angular/common';
import { Component } from '@angular/core';
import { type Observable } from 'rxjs';

import {
  ModalSectionKey,
  type ModalFooterData,
} from '../../../modal.interface';

import { ModalSectionComponent } from '../abstract/modal-section.component';

/**
 * Every button this footer routes through {@link DefaultFooterComponent.click},
 * and whether it takes the modal's action rather than dismissing it. `ok`,
 * `confirmWarning` and `confirmInfo` are absent: they submit the form, so they
 * resolve through the modal's own submit handler.
 */
const FOOTER_ACTION_RESOLVES = {
  action: true,
  cancel: false,
  close: false,
  delete: true,
  no: false,
  yes: true,
} as const satisfies Record<string, boolean>;

type FooterAction = keyof typeof FOOTER_ACTION_RESOLVES;

@Component({
  selector: 'myre-modal-footer-default',
  templateUrl: './app-modal-footer-default.component.html',
  styleUrls: ['./app-modal-footer-default.component.scss'],
  imports: [ButtonComponent, TranslatePipe, ActionIdDirective, AsyncPipe],
})
export class DefaultFooterComponent extends ModalSectionComponent<ModalFooterData> {
  public isLoading$: Observable<boolean>;
  protected _name = ModalSectionKey.Footer;

  public click(action: FooterAction): void {
    if (FOOTER_ACTION_RESOLVES[action]) {
      this._modalService.markResolved(this.uid());
    }

    const clickHandler = (this._context() as Record<string, unknown>)[
      `${action}Click`
    ];

    if ('function' === typeof clickHandler) {
      return clickHandler.call(this._context());
    }
  }

  public actionLoading(): boolean {
    const loading = this.data().action?.loading;

    return Boolean(loading && loading());
  }

  public actionDisabled(): boolean {
    const disabled = this.data().action?.disabled;

    return Boolean(disabled && disabled());
  }

  public okDisabled(): boolean {
    if (this._context()?.formGroup) {
      // if form has control(s) it should have at least one element dirty & valid
      return (
        Object.keys(this._context().formGroup.controls).length &&
        !(
          this._context().formGroup &&
          this._context().formGroup.valid &&
          this._context().formGroup.dirty
        )
      );
    }

    return false;
  }

  protected readonly ButtonTheme = ButtonTheme;
  protected readonly ButtonSubTheme = ButtonSubTheme;
  protected readonly ButtonType = ButtonType;
}
